/* GET /api/tiles/token - 15-api-contract.md section 3.12. Where the basemap and
 * the imagery are, the signature that reads them, and which archives are
 * current.
 *
 * The archives are learned here at runtime rather than compiled in, because a
 * build publishes under a new name and a name baked into the bundle would keep
 * every browser on the old one until the next deploy (02 § PMTiles serving).
 *
 * A credential, so it lives in the query cache for the session and nowhere
 * that outlasts it (15 section 3.12).
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { fetchJson } from "./envelope";
import type { ImageryCell, TilesToken } from "./types";

export async function getTilesToken(): Promise<TilesToken> {
  return fetchJson<TilesToken>("/api/tiles/token", (status) => `the tiles token was refused (${status})`);
}

/* The one query every reader of the token shares, so the basemap and the
 * imagery never ask for it twice. */
function useTilesToken() {
  return useQuery({
    queryKey: ["tiles-token"],
    queryFn: getTilesToken,
    staleTime: Infinity,
    retry: false,
  });
}

/* An archive's URL in the basemap container - `{base_url}/{container}/{archive}?{sas}`. */
function archiveUrl(token: TilesToken, archive: string): string {
  const { name, sas } = token.containers.basemap;
  return `${token.base_url}/${name}/${archive}?${sas}`;
}

/* The basemap archive's URL, or null where the container holds no archive. */
export function basemapUrl(token: TilesToken): string | null {
  const { archive } = token.containers.basemap;
  return archive === null ? null : archiveUrl(token, archive);
}

/* The basemap for a map to stand on, once known: `url` is undefined while the
 * token is read, then a URL, or null with `problem` saying why there is none.
 * A map drawn without a basemap says so rather than showing a blank ground. */
export function useBasemapUrl(): { url: string | null | undefined; problem: string | null } {
  const { data, error } = useTilesToken();
  if (error) return { url: null, problem: `The basemap is unavailable: ${error.message}` };
  if (data === undefined) return { url: undefined, problem: null };
  const url = basemapUrl(data);
  return { url, problem: url === null ? "No basemap has been published." : null };
}

export interface ImagerySource extends ImageryCell {
  url: string;
}

/* Every imagery cell with the URL that reads it. */
export function imagerySources(token: TilesToken): ImagerySource[] {
  return token.imagery.map((cell) => ({ ...cell, url: archiveUrl(token, cell.archive) }));
}

/* The territory's imagery archive's URL, or null where none is held. */
export function usImageryUrl(token: TilesToken): string | null {
  return token.us_imagery === null ? null : archiveUrl(token, token.us_imagery);
}

export interface Imagery {
  /* The territory beneath the cells' zoom, or null where none is held. */
  us: string | null;
  cells: ImagerySource[];
}

/* The imagery once the token is known, and none until then. Memoised on the
 * token, so an effect depending on it runs when the imagery changes rather
 * than on every render. */
export function useImagery(): Imagery {
  const { data } = useTilesToken();
  return useMemo(
    () => (data === undefined ? { us: null, cells: [] } : { us: usImageryUrl(data), cells: imagerySources(data) }),
    [data],
  );
}
